import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as UserIcon } from '@/assets/icons/user-plus.svg';
import BackButton from '@/components/BackButton';
import Button from '@/components/Button';
import ContentSpinner from '@/components/ContentSpinner';
import { PageHeading } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import { FreelancerDetailsResource } from '@/types/resources';
import { api } from '@/utils/api';
import {
  calculateAge,
  renderCountryName,
  renderRecipientTypeName,
} from '@/utils/helpers';

import FreelancerBanksModal from './components/FreelancerBanksModal';
import FreelancerDepartments from './components/FreelancerDepartments';
import FreelancerFiles from './components/FreelancerFiles';
import FreelancerPersonalDetailsModal from './components/FreelancerPersonalDetailsModal';
import FreelancerVacation from './components/vacation/FreelancerVacation';

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
  button {
    flex-shrink: 0;
  }
`;

const HeadingHolder = styled.div`
  display: flex;
  margin-bottom: 32px;
  margin-top: 3px;
  gap: 12px;
  align-items: center;
`;

const StyledPageHeading = styled(PageHeading)`
  margin: 0;
`;

const BanksRow = styled.div`
  display: flex;
  flex: 1;
  padding-bottom: 24px;
  border-bottom: solid 1px ${({ theme }) => theme.colors.lightGray};
  &:last-child {
    padding-bottom: 0;
    padding-top: 24px;
    border-bottom: 0;
  }
  &:first-child {
    padding-top: 0;
  }
`;

export const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  padding: 24px;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  display: flex;
  margin-bottom: 56px;
`;

const BanksBox = styled(Box)`
  display: block;
`;

export const NoDetailsBox = styled(Box)`
  justify-content: center;
  align-items: center;
  min-height: 182px;
`;

export const NoDetailsText = styled(Text)`
  display: flex;
  align-items: center;
  svg {
    margin-right: 8px;
  }
`;

export const Column = styled.div`
  flex: 1;
  display: grid;
  grid-gap: 22px;
  align-content: start;
  padding-right: 24px;
`;

export const ColumnRight = styled(Column)`
  border-left: 1px solid ${({ theme }) => theme.colors.lightGray};
  padding-right: 0;
  padding-left: 24px;
`;

const Heading = styled(Text)`
  margin-bottom: 6px;
`;

const StyledText = styled(Text)`
  line-height: 22px;
  span {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

export const BoxHeadingRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
`;

export const BoxHeadingBrake = styled.div`
  flex: 1;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.lightGray};
  margin: 0 16px;
`;

export const EditButton = styled(Button)`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  font-size: 11px;
  line-height: 15px;
  height: 27px;
  padding: 0 15px;
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const UserBadge = styled(Text)`
  background: rgba(65, 54, 207, 0.08);
  border-radius: 100px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
`;

const StyledButton = styled(Button)`
  border-radius: 100px;
`;

const Freelancer = () => {
  const [showPersonalDetailsModal, setShowPersonalDetailsModal] =
    useState(false);

  const [showBanksModal, setShowBanksModal] = useState(false);

  const [isCreatingUser, setIsCreatingUser] = useState(false);

  const { id } = useParams();

  const navigate = useNavigate();

  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const userCanSeeData = useHasPermission('view all freelancers');
  const userCanManageData = useHasPermission('manage freelancers');

  const { isLoading, data: freelancer } = useQuery<FreelancerDetailsResource>(
    ['freelancers_details', { id }],
    () =>
      api.get<never, FreelancerDetailsResource>(
        `/v1/payments/freelancers/${id}`,
      ),
    {
      staleTime: 60 * 1000,
      retry: 2,
      onError: (err) => {
        if ((err as any).response.status === 404) {
          return navigate('/404', {
            replace: true,
          });
        }
        alerts.error();
      },
    },
  );

  const handleAddAsUser = async () => {
    if (!freelancer?.workEmail) {
      alerts.error('Freelancer has no work email');
      return;
    }
    setIsCreatingUser(true);
    try {
      await api.post('/v2/users', {
        email: freelancer.workEmail,
        role: 'Team Member',
        assign_to_all_teams: false,
        teams: [],
        permissions: [],
        production_teams: [],
        assign_to_all_production_teams: false,
      });
      await queryClient.invalidateQueries(['freelancers_details', { id }]);
      alerts.success('User successfully created');
    } catch (error) {
      alerts.error();
    } finally {
      setIsCreatingUser(false);
    }
  };

  if (isLoading || !freelancer) {
    return <ContentSpinner />;
  }

  return (
    <>
      <Text $color="gray" $type="caption">
        ACCOUNT
      </Text>
      <HeadingRow>
        <HeadingHolder>
          <StyledPageHeading>{freelancer.member.name}</StyledPageHeading>
          {freelancer.isUser ? (
            <UserBadge $type="smallBold" $color="purple">
              Backbone user
            </UserBadge>
          ) : (
            <StyledButton
              small
              loading={isCreatingUser}
              disabled={isCreatingUser}
              onClick={handleAddAsUser}
            >
              <UserIcon />
              Add as a Backbone user
            </StyledButton>
          )}
        </HeadingHolder>
      </HeadingRow>
      <ButtonsRow>
        <BackButton />
      </ButtonsRow>
      {userCanSeeData && (
        <>
          <BoxHeadingRow>
            <Text $color="gray" $type="smallHeadline">
              PERSONAL DETAILS
            </Text>
            <BoxHeadingBrake />
            {userCanManageData && (
              <EditButton
                small
                color="secondary"
                onClick={() => setShowPersonalDetailsModal(true)}
              >
                Edit
              </EditButton>
            )}
          </BoxHeadingRow>
          <Box>
            <Column>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Name Surname
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.member.name}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Representative
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.member.representative ?? '-'}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Personal Code
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.personalCode ?? '-'}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Activity Code
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.activityCode ?? '-'}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  VAT Number
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.member.vat_number ?? '-'}
                </StyledText>
              </div>

              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Country
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.member.country
                    ? renderCountryName(freelancer.member.country)
                    : '-'}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Postcode
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.member.post_code ?? '-'}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Category
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.category ?? '-'}
                </StyledText>
              </div>
            </Column>
            <ColumnRight>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Date of Birth
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.dateOfBirth ? (
                    <>
                      {DateTime.fromISO(freelancer.dateOfBirth).toFormat(
                        'yyyy-LL-dd',
                      )}
                      <span>
                        {' '}
                        · {calculateAge(freelancer.dateOfBirth)} years old
                      </span>
                    </>
                  ) : (
                    '-'
                  )}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Gender
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.gender ?? '-'}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Personal Email
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.personalEmail ?? '-'}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Phone Number
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.phoneNumber ?? '-'}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Address
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.member.address ?? '-'}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Company Address
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.member.company_address ?? '-'}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  City
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.member.city ?? '-'}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Work Email
                </Heading>
                <StyledText $type="captionSemibold">
                  {freelancer.workEmail ?? '-'}
                </StyledText>
              </div>
            </ColumnRight>
          </Box>
        </>
      )}
      {userCanSeeData && (
        <>
          <BoxHeadingRow>
            <Text $color="gray" $type="smallHeadline">
              BANK DETAILS
            </Text>
            <BoxHeadingBrake />
            {userCanManageData && (
              <EditButton
                small
                color="secondary"
                onClick={() => setShowBanksModal(true)}
              >
                {freelancer.member.banks.length ? 'Edit' : 'Add'}
              </EditButton>
            )}
          </BoxHeadingRow>

          {freelancer.member.banks.length ? (
            <BanksBox>
              {freelancer.member.banks.map((bank) => (
                <BanksRow key={bank.id}>
                  <Column>
                    {bank.true_receiver && (
                      <div>
                        <Heading $type="smallHeadline" $color="gray">
                          True Receiver
                        </Heading>
                        <StyledText $type="captionSemibold">
                          {bank.true_receiver}
                        </StyledText>
                      </div>
                    )}
                    <div>
                      <Heading $type="smallHeadline" $color="gray">
                        Creditor Bank
                      </Heading>
                      <StyledText $type="captionSemibold">
                        {bank.bank ?? '-'}
                      </StyledText>
                    </div>
                    <div>
                      <Heading $type="smallHeadline" $color="gray">
                        Swift Code
                      </Heading>
                      <StyledText $type="captionSemibold">
                        {bank.swift_code ?? '-'}
                      </StyledText>
                    </div>
                    <div>
                      <Heading $type="smallHeadline" $color="gray">
                        Payment Type
                      </Heading>
                      <StyledText $type="captionSemibold">
                        {bank.payment_type ?? '-'}
                      </StyledText>
                    </div>
                    <div>
                      <Heading $type="smallHeadline" $color="gray">
                        Bank Address
                      </Heading>
                      <StyledText $type="captionSemibold">
                        {bank.bank_address ?? '-'}
                      </StyledText>
                    </div>
                  </Column>
                  <ColumnRight>
                    <div>
                      <Heading $type="smallHeadline" $color="gray">
                        Account Number
                      </Heading>
                      <StyledText $type="captionSemibold">
                        {bank.iban ?? '-'}
                      </StyledText>
                    </div>
                    <div>
                      <Heading $type="smallHeadline" $color="gray">
                        Recipient Type
                      </Heading>
                      <StyledText $type="captionSemibold">
                        {bank.recipient_type
                          ? renderRecipientTypeName(bank.recipient_type)
                          : '-'}
                      </StyledText>
                    </div>
                    <div>
                      <Heading $type="smallHeadline" $color="gray">
                        Bank Country
                      </Heading>
                      <StyledText $type="captionSemibold">
                        {bank.bank_country
                          ? renderCountryName(bank.bank_country)
                          : '-'}
                      </StyledText>
                    </div>
                    <div>
                      <Heading $type="smallHeadline" $color="gray">
                        Note
                      </Heading>
                      <StyledText $type="captionSemibold">
                        {bank.note ?? '-'}
                      </StyledText>
                    </div>
                  </ColumnRight>
                </BanksRow>
              ))}
            </BanksBox>
          ) : (
            <NoDetailsBox>
              <NoDetailsText $type="caption" $color="gray">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.40625 6.66675H13.3322"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7.40625 9.6297H13.3322"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7.40625 12.5927H10.3692"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.023 18.5185H2.95949C2.76303 18.5185 2.57462 18.4369 2.43571 18.2917C2.29679 18.1464 2.21875 17.9495 2.21875 17.7441V2.25586C2.21875 2.05047 2.29679 1.8535 2.43571 1.70827C2.57462 1.56303 2.76303 1.48145 2.95949 1.48145H17.7743C17.9708 1.48145 18.1592 1.56303 18.2981 1.70827C18.437 1.8535 18.515 2.05047 18.515 2.25586V12.7768C18.515 12.8785 18.4959 12.9792 18.4587 13.0732C18.4214 13.1671 18.3669 13.2525 18.2981 13.3244L13.5468 18.2917C13.478 18.3636 13.3964 18.4206 13.3065 18.4595C13.2166 18.4985 13.1203 18.5185 13.023 18.5185V18.5185Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M18.4463 12.5918H13.3281V17.7103"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                No details added
              </NoDetailsText>
            </NoDetailsBox>
          )}
        </>
      )}
      <AnimatePresence>
        {userCanManageData && showPersonalDetailsModal && (
          <FreelancerPersonalDetailsModal
            freelancer={freelancer}
            onClose={() => {
              setShowPersonalDetailsModal(false);
            }}
          />
        )}
        {showBanksModal && (
          <FreelancerBanksModal
            freelancer={freelancer}
            onClose={() => {
              setShowBanksModal(false);
            }}
          />
        )}
      </AnimatePresence>
      <FreelancerDepartments departments={freelancer.departmentDetails} />
      {(userCanManageData || freelancer.files) && (
        <FreelancerFiles
          files={freelancer.files}
          canManage={userCanManageData}
        />
      )}
      <FreelancerVacation />
    </>
  );
};

export default Freelancer;
